import React, { useState, useEffect } from 'react';
import { FaTimes, FaCamera, FaSave, FaUserShield, FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../../utils/api.service';
import { toast } from 'react-toastify';
import './AdminProfileModal.css';

const AdminProfileModal = ({ isOpen, onClose, adminData, onUpdateSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    oldPassword: '',
    password: '',
    confirmPassword: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (adminData && isOpen) {
      setFormData({
        firstName: adminData.firstName || '',
        lastName: adminData.lastName || '',
        email: adminData.email || '',
        oldPassword: '',
        password: '',
        confirmPassword: ''
      });
      setError(null);
    }
  }, [adminData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adminData?.id) return;

    setLoading(true);
    setError(null);

    console.log(`[AdminProfileSubmit] Attempting update for ID: ${adminData.id}`);

    try {
      // Build exactly what we should send (excluding empty password)
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      };

      const hasOldPassword = formData.oldPassword && formData.oldPassword.trim().length > 0;
      const hasNewPassword = formData.password && formData.password.trim().length > 0;
      const hasConfirmPassword = formData.confirmPassword && formData.confirmPassword.trim().length > 0;

      if (hasOldPassword || hasNewPassword || hasConfirmPassword) {
        if (!hasOldPassword) {
          setError('Previous password is required to change your password.');
          setLoading(false);
          return;
        }
        if (!hasNewPassword) {
          setError('New password cannot be empty if you are changing it.');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('New password and Confirm password do not match.');
          setLoading(false);
          return;
        }
        payload.password = formData.password;
        payload.oldPassword = formData.oldPassword;
      }

      console.log('[AdminProfileSubmit] Payload:', { ...payload, password: payload.password ? '********' : undefined });

      await api.patch(`/admins/${adminData.id}`, payload);

      if (payload.password) {
        toast.success('Profile & password updated successfully!');
      } else {
        toast.success('Profile updated successfully!');
      }

      onUpdateSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update admin profile:', err);

      let errMsg = 'Failed to update profile. Please try again.';
      if (err.response?.data?.message) {
        if (Array.isArray(err.response.data.message)) {
          errMsg = err.response.data.message[0];
        } else {
          errMsg = err.response.data.message;
        }
      }

      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <div className="admin-modal-title">
            <FaUserShield className="header-icon" />
            <h2>Edit Admin Profile</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-modal-form">
          <div className="modal-form-body">
            <div className="admin-profile-header-card">
              <div className="admin-avatar-wrapper">
                <div className="admin-avatar">
                  {formData.firstName?.[0]}{formData.lastName?.[0]}
                </div>
                {/* <div className="admin-avatar-upload">
                  <FaCamera />
                </div> */}
              </div>
              <div className="admin-role-badge">
                {adminData?.role ? adminData.role.toUpperCase() : 'ADMIN'}
              </div>
            </div>

            {error && <div className="alert-error">{error}</div>}

            {/* SECTION 1: PERSONAL INFORMATION */}
            <div className="form-section">
              <div className="section-title">Personal Information</div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="modal-input"
                    placeholder="Enter first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="modal-input"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="modal-input"
                  placeholder="admin@example.com"
                />
                <small className="input-hint">Changing your email will affect how you log in.</small>
              </div>
            </div>

            {/* SECTION 2: SECURITY SETTINGS */}
            <div className="form-section">
              <div className="section-title">Security Settings</div>
              <div className="form-group">
                <label>Previous Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    name="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    className="modal-input"
                    placeholder="Enter current password to make changes"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                  >
                    {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="modal-input"
                    placeholder="Leave blank to keep same"
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <small className="input-hint">Min. 6 characters.</small>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="modal-input"
                    placeholder="Confirm new password"
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" /> Saving...
                </>
              ) : (
                <>
                  <FaSave style={{ marginRight: '8px' }} /> Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProfileModal;
